import GameStateManager from '../gameStateManager';
import { RoomState, GameMode, Player } from '../../shared/sharedTypes';
import { calculateCoinDashScore } from '../../shared/utils';
import BallManager from '../ballManager';
import { formatDisplayHeight } from '../utils';

export default class GameModeUI {
    private socketClient: any;
    private gameStateManager: GameStateManager;
    private roomState: RoomState;
    private ballManager: BallManager;
    private container: HTMLDivElement;
    private updateInterval: number;
    private timeValueEl: HTMLElement | null = null;
    private parValueEl: HTMLElement | null = null;
    private parRowEl: HTMLElement | null = null;
    private pillMap: Record<string, HTMLElement | null> = {
        mode: null,
        coins: null,
        hx: null,
        score: null,
    };
    private static stylesInjected = false;

    constructor(socketClient: any, gameStateManager: GameStateManager, initialRoomState: RoomState, ballManager: BallManager) {
        this.socketClient = socketClient;
        this.gameStateManager = gameStateManager;
        this.roomState = initialRoomState;
        this.ballManager = ballManager;
        this.container = document.createElement('div');
        this.container.className = 'golfup-hud';
        this.render();
        this.updateInterval = window.setInterval(() => this.update(), 1000 / 60); // 60 FPS update
    }

    private render(): void {
        if (!GameModeUI.stylesInjected) {
            this.injectStyles();
        }

        this.container.innerHTML = `
            <div class="golfup-hud-card">
                <div class="golfup-hud-card__header">
                    <span>TIME</span>
                    <div class="golfup-hud-card__dot"></div>
                </div>
                <div class="golfup-hud-time" data-golfup="time">00:00</div>
                <div class="golfup-hud-card__footer" data-golfup="par-row">
                    <span>PAR</span>
                    <span class="golfup-hud-par-value" data-golfup="par">--</span>
                </div>
            </div>
            <div class="golfup-hud-pills">
                <span class="golfup-pill" data-pill="mode" data-visible="false">MODE</span>
                <span class="golfup-pill" data-pill="coins" data-visible="false">COINS</span>
                <span class="golfup-pill" data-pill="hx" data-visible="false">HX</span>
                <span class="golfup-pill" data-pill="score" data-visible="false">SCORE</span>
            </div>
        `;

        this.timeValueEl = this.container.querySelector('[data-golfup="time"]');
        this.parValueEl = this.container.querySelector('[data-golfup="par"]');
        this.parRowEl = this.container.querySelector('[data-golfup="par-row"]');
        this.pillMap.mode = this.container.querySelector('[data-pill="mode"]');
        this.pillMap.coins = this.container.querySelector('[data-pill="coins"]');
        this.pillMap.hx = this.container.querySelector('[data-pill="hx"]');
        this.pillMap.score = this.container.querySelector('[data-pill="score"]');

        document.body.appendChild(this.container);
    }

    private injectStyles(): void {
        const hasStyle = document.querySelector('style[data-golfup-hud]');
        if (hasStyle) {
            GameModeUI.stylesInjected = true;
            return;
        }
        const style = document.createElement('style');
        style.dataset.golfupHud = 'true';
        style.textContent = `
            @media (max-width: 640px) {
                .golfup-hud-card {
                    width: calc(100vw - 2.5rem);
                    transform: none;
                }
                .golfup-hud {
                    left: 1rem;
                    transform: none;
                    right: 1rem;
                }
            }
        `;
        document.head.appendChild(style);
        GameModeUI.stylesInjected = true;
    }

    private update(): void {
        if (this.gameStateManager.isroundEnded || this.gameStateManager.isGameOver) return;

        const currentRoomState = this.gameStateManager.roomState;
        this.roomState = currentRoomState;
        const timeRemaining = Math.floor(this.gameStateManager?.getRoundTimeRemaining() / 1000);
        const currentPlayer = this.gameStateManager.getCurrentPlayer();
        const round = currentRoomState.round;
        const gameMode = currentRoomState.roomSettings.gameMode;

        this.updateModeInfo();
        this.updateTimeInfo(timeRemaining);
        this.updateAdditionalInfo(gameMode, currentPlayer, round);
    }

    private updateModeInfo(): void {
        const pill = this.pillMap.mode;
        if (!pill) return;

        let text = this.ballManager.gameModeHandler.getModeName().toUpperCase();
        if (this.ballManager.gameModeHandler.hasRounds()) {
            text = `${text} (${this.roomState.round + 1}/${this.roomState.totalRounds})`;
        }
        pill.textContent = text;
        pill.dataset.visible = 'true';
    }

    private updateTimeInfo(timeRemaining: number): void {
        if (!this.timeValueEl) return;
        if (!this.ballManager.gameModeHandler.hasTimer()) {
            this.timeValueEl.textContent = '--:--';
            return;
        }
        const safeTime = Math.max(0, timeRemaining);
        const minutes = Math.floor(safeTime / 60).toString().padStart(2, '0');
        const seconds = (safeTime % 60).toString().padStart(2, '0');
        this.timeValueEl.textContent = `${minutes}:${seconds}`;
    }

    private updateAdditionalInfo(gameMode: GameMode, currentPlayer: Player | null, round: number): void {
        const visibleElements = this.ballManager.gameModeHandler.getVisibleUIElements();

        if (visibleElements.includes('par')) {
            const par = currentPlayer?.scoring.clicks[round] ?? 0;
            if (this.parValueEl) this.parValueEl.textContent = par.toString();
            if (this.parRowEl) this.parRowEl.classList.remove('hidden');
        } else {
            if (this.parValueEl) this.parValueEl.textContent = '--';
            if (this.parRowEl) this.parRowEl.classList.add('hidden');
        }

        this.updatePill('coins', visibleElements.includes('coins') ? (currentPlayer?.scoring?.coins[round] ?? 0).toString() : null, 'COINS');
        this.updatePill('hx', visibleElements.includes('hx') ? formatDisplayHeight(currentPlayer?.highestHeight ?? 0, 0) : null, 'HX');

        if (visibleElements.includes('score')) {
            const coins = currentPlayer?.scoring?.coins[round] || 0;
            const height = currentPlayer?.highestHeight || 0;
            const score = calculateCoinDashScore(coins, height);
            this.updatePill('score', score.toString(), 'SCORE');
        } else {
            this.updatePill('score', null, 'SCORE');
        }
    }

    private updatePill(key: keyof typeof this.pillMap, value: string | null, label: string): void {
        const pill = this.pillMap[key];
        if (!pill) return;
        if (value === null) {
            pill.dataset.visible = 'false';
            return;
        }
        pill.textContent = `${label}: ${value}`;
        pill.dataset.visible = 'true';
    }

    public destroy(): void {
        clearInterval(this.updateInterval);
        if (this.container && this.container.parentNode) {
            this.container.parentNode.removeChild(this.container);
        }
    }
}